import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Briefcase, Pencil, Plus, ShieldAlert } from 'lucide-react'

import { api, ApiError } from '@/lib/api'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Toast, ToastDescription, ToastTitle } from '@/components/ui/toast'
import { CarteiraDialog } from '@/components/CarteiraDialog'
import { UserFormDialog, type UserFormValues } from '@/components/UserFormDialog'
import { useAuthStore } from '@/stores/auth'
import type { SellerListResponse, User, UserListResponse } from '@sdr-agend/admin-schemas'

interface ToastState {
  open: boolean
  variant: 'default' | 'destructive'
  title: string
  description?: string
}

const ROLE_BADGE: Record<string, 'default' | 'secondary' | 'outline'> = {
  admin: 'default',
  manager: 'secondary',
  operator: 'outline',
  viewer: 'outline',
  vendedor: 'secondary',
  sdr: 'secondary',
}

function formatDate(value: string | null): string {
  if (value === null) return '—'
  try {
    return new Date(value).toLocaleString('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    })
  } catch {
    return value
  }
}

/**
 * `/admin/users` — listagem + criação + edição de usuários do painel.
 *
 * RBAC: somente `admin`. Story 1.7 §AC #5..#7 (backend) + UI nova.
 */
export function UsersPage(): React.ReactElement {
  const role = useAuthStore((s) => s.user?.role)
  const currentUserId = useAuthStore((s) => s.user?.id)
  const queryClient = useQueryClient()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<User | null>(null)
  const [toast, setToast] = useState<ToastState>({ open: false, variant: 'default', title: '' })
  // Story 12.6 (Epic E12) — carteira dialog state.
  const [carteiraOpen, setCarteiraOpen] = useState(false)
  const [carteiraSdr, setCarteiraSdr] = useState<User | null>(null)

  const { data, isLoading, isError, error } = useQuery<UserListResponse>({
    queryKey: ['admin', 'users'],
    queryFn: async () => api<UserListResponse>('/admin/users'),
    enabled: role === 'admin',
  })

  // 2026-07-21 (user request) — sellers list feeds the "linkar vendedor"
  // picker in UserFormDialog when creating a role='vendedor' login.
  const { data: sellersData } = useQuery<SellerListResponse>({
    queryKey: ['admin', 'sellers'],
    queryFn: async () => api<SellerListResponse>('/admin/sellers'),
    enabled: role === 'admin',
  })

  const createMutation = useMutation({
    mutationFn: async (values: UserFormValues): Promise<void> => {
      await api('/admin/users', {
        method: 'POST',
        body: JSON.stringify({
          email: values.email,
          password: values.password,
          name: values.name,
          role: values.role,
          active: values.active,
          sellerId: values.sellerId,
        }),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      setDialogOpen(false)
      setToast({ open: true, variant: 'default', title: 'Usuário criado com sucesso.' })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: UserFormValues }): Promise<void> => {
      const payload: Record<string, unknown> = {
        name: values.name,
        role: values.role,
        active: values.active,
      }
      if (values.password !== '') {
        payload.password = values.password
      }
      await api(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      setDialogOpen(false)
      setEditing(null)
      setToast({ open: true, variant: 'default', title: 'Usuário atualizado com sucesso.' })
    },
  })

  if (role !== 'admin') {
    return <Navigate to="/dashboard" replace />
  }

  const submitting = createMutation.isPending || updateMutation.isPending

  const handleSubmit = async (values: UserFormValues): Promise<void> => {
    if (editing) {
      await updateMutation.mutateAsync({ id: editing.id, values })
    } else {
      await createMutation.mutateAsync(values)
    }
  }

  const openCreate = (): void => {
    setEditing(null)
    setDialogOpen(true)
  }

  const openEdit = (user: User): void => {
    setEditing(user)
    setDialogOpen(true)
  }

  const handleToggleActive = async (user: User): Promise<void> => {
    try {
      await updateMutation.mutateAsync({
        id: user.id,
        values: {
          email: user.email,
          name: user.name,
          role: user.role,
          active: !user.active,
          password: '',
        },
      })
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `Erro ${err.status}: ${err.bodyText}`
          : err instanceof Error
            ? err.message
            : 'Erro desconhecido.'
      setToast({
        open: true,
        variant: 'destructive',
        title: 'Falha ao atualizar usuário',
        description: message,
      })
    }
  }

  const users = data?.users ?? []

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Usuários</h1>
          <p className="text-sm text-muted-foreground">
            Gerencie quem tem acesso ao painel administrativo. Apenas <code>admin</code> pode
            criar/editar usuários.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          Novo usuário
        </Button>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Último login</TableHead>
              <TableHead className="w-32 text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {isError && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-destructive">
                  <ShieldAlert className="mr-2 inline-block h-4 w-4" aria-hidden="true" />
                  Erro ao carregar usuários: {(error as Error)?.message ?? 'desconhecido'}
                </TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                  Nenhum usuário encontrado.
                </TableCell>
              </TableRow>
            )}
            {users.map((u) => {
              const isSelf = u.id === currentUserId
              return (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">
                    {u.name}
                    {isSelf && (
                      <span className="ml-2 text-xs text-muted-foreground">(você)</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant={ROLE_BADGE[u.role] ?? 'outline'}>{u.role}</Badge>
                  </TableCell>
                  <TableCell>
                    {u.active ? (
                      <Badge variant="outline" className="border-green-500 text-green-700 dark:text-green-400">
                        Ativo
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="border-muted-foreground text-muted-foreground">
                        Inativo
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(u.lastLoginAt)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {u.role === 'sdr' && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setCarteiraSdr(u)
                            setCarteiraOpen(true)
                          }}
                          aria-label={`Ver carteira de ${u.name}`}
                          title="Ver/reatribuir carteira de leads"
                        >
                          <Briefcase className="h-4 w-4" aria-hidden="true" />
                          Carteira
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => openEdit(u)}
                        aria-label={`Editar ${u.name}`}
                      >
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                        Editar
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={isSelf || submitting}
                        onClick={() => void handleToggleActive(u)}
                        title={
                          isSelf
                            ? 'Você não pode desativar a si mesmo'
                            : u.active
                              ? 'Desativar usuário'
                              : 'Reativar usuário'
                        }
                      >
                        {u.active ? 'Desativar' : 'Ativar'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <UserFormDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open)
          if (!open) setEditing(null)
        }}
        user={editing}
        onSubmit={handleSubmit}
        submitting={submitting}
        sellers={sellersData?.sellers ?? []}
      />

      <CarteiraDialog
        open={carteiraOpen}
        onOpenChange={(open) => {
          setCarteiraOpen(open)
          if (!open) setCarteiraSdr(null)
        }}
        sdr={carteiraSdr}
        sdrs={users.filter((u) => u.role === 'sdr' && u.active)}
      />

      <Toast
        open={toast.open}
        variant={toast.variant}
        onOpenChange={(open) => setToast((s) => ({ ...s, open }))}
      >
        <ToastTitle>{toast.title}</ToastTitle>
        {toast.description !== undefined && <ToastDescription>{toast.description}</ToastDescription>}
      </Toast>
    </div>
  )
}
